import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FilterProvider } from "@/context/FilterContext";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Overview from "@/pages/Overview";
import Reservations from "@/pages/Reservations";
import Inquiries from "@/pages/Inquiries";
import Signups from "@/pages/Signups";
import Revenue from "@/pages/Revenue";
import SEO from "@/pages/SEO";
import Marketing from "@/pages/Marketing";
import Visitors from "@/pages/Visitors";
import UsersPage from "@/pages/Users";
import PublicLayout from "@/components/public/PublicLayout";
import Home from "@/pages/public/Home";
import Portfolio from "@/pages/public/Portfolio";
import PropertyDetail from "@/pages/public/PropertyDetail";
import Experiences from "@/pages/public/Experiences";
import Story from "@/pages/public/Story";
import Happenings from "@/pages/public/Happenings";
import EventDetail from "@/pages/public/EventDetail";
import Press from "@/pages/public/Press";
import Scholarships from "@/pages/public/Scholarships";
import Contact from "@/pages/public/Contact";

function FullLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--pb-bg)]">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#526E7E] border-t-transparent" />
        <p className="text-sm text-[rgba(48,56,60,0.6)]">Loading command center…</p>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequireCorporate({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "corporate") return <Navigate to="/admin" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public marketing website */}
      <Route element={<PublicLayout />}>
        <Route index element={<Home />} />
        <Route path="portfolio" element={<Portfolio />} />
        <Route path="portfolio/:slug" element={<PropertyDetail />} />
        <Route path="experiences" element={<Experiences />} />
        <Route path="story" element={<Story />} />
        <Route path="happenings" element={<Happenings />} />
        <Route path="happenings/:slug" element={<EventDetail />} />
        <Route path="press" element={<Press />} />
        <Route path="scholarships" element={<Scholarships />} />
        <Route path="contact" element={<Contact />} />
      </Route>

      {/* Team login */}
      <Route path="/login" element={<Login />} />

      {/* Team console (existing dashboard) */}
      <Route
        path="/admin"
        element={
          <Protected>
            <FilterProvider>
              <AppShell />
            </FilterProvider>
          </Protected>
        }
      >
        <Route index element={<Overview />} />
        <Route path="reservations" element={<Reservations />} />
        <Route path="inquiries" element={<Inquiries />} />
        <Route path="signups" element={<Signups />} />
        <Route path="revenue" element={<Revenue />} />
        <Route path="seo" element={<SEO />} />
        <Route path="marketing" element={<Marketing />} />
        <Route
          path="visitors"
          element={
            <RequireCorporate>
              <Visitors />
            </RequireCorporate>
          }
        />
        <Route
          path="users"
          element={
            <RequireCorporate>
              <UsersPage />
            </RequireCorporate>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
